import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, JsonObjectSchema, UuidSchema } from './common.js';
/**
 * Shared TypeBox schemas for Hub-owned scheduled agent actions (COP-074/COP-076/COP-077).
 *
 * These contracts intentionally expose owner-scoped schedule management state only:
 * prompt text is visible to the owning user, while run history carries redacted
 * error/metadata summaries and never full tool payloads.
 */
/** COP-074: v1.1 accepts exactly 5 cron fields: minute hour day-of-month month day-of-week. */
export const ScheduleExprSchema = Type.String({
    minLength: 9,
    maxLength: 200,
    pattern: '^\\S+\\s+\\S+\\s+\\S+\\s+\\S+\\s+\\S+$',
});
/** Conservative syntactic guard for Area/Location style IANA timezone ids. */
export const ScheduleTimezoneSchema = Type.String({
    minLength: 3,
    maxLength: 100,
    pattern: '^[A-Za-z][A-Za-z0-9_+-]*(?:/[A-Za-z0-9_+-]+)+$',
});
export const SchedulePromptSchema = Type.String({ minLength: 1, maxLength: 16_384 });
export const ScheduleToolNameSchema = Type.String({
    minLength: 1,
    maxLength: 200,
    pattern: '^[A-Za-z0-9_.:-]+$',
});
export const ScheduleToolAllowlistSchema = Type.Array(ScheduleToolNameSchema, {
    minItems: 1,
    maxItems: 100,
    uniqueItems: true,
});
/**
 * Same shape as ScheduleToolAllowlistSchema but permits an empty array.
 * Used ONLY by the chat-side draft-preview surface (SchedulePreviewPartSchema)
 * so the `create_schedule` executor can emit a preview card even when no
 * allowlist is derivable on the first turn (Set A ∩ Set B ∩ proposed all
 * empty). The user picks tools from the card's dropdown before Approve;
 * the decideApprove path enforces a non-empty allowlist before persisting
 * to `tf_scheduled_action`, so D-06 still holds at the persistence layer.
 */
export const ScheduleDraftToolAllowlistSchema = Type.Array(ScheduleToolNameSchema, {
    minItems: 0,
    maxItems: 100,
    uniqueItems: true,
});
/** Path params shared by schedule item and run-history routes. */
export const ScheduleIdParamsSchema = Type.Object({
    id: UuidSchema,
}, { additionalProperties: false });
/** `POST /schedules` body (COP-077). */
export const ScheduleCreateBodySchema = Type.Object({
    trackId: UuidSchema,
    scheduleExpr: ScheduleExprSchema,
    timezone: ScheduleTimezoneSchema,
    prompt: SchedulePromptSchema,
    toolAllowlist: ScheduleToolAllowlistSchema,
    wholePlanPreapproved: Type.Boolean(),
    enabled: Type.Optional(Type.Boolean()),
    /**
     * COP-134 D3 / ATRU-03 — opt into the `scheduled_succeeded` notification.
     * OPTIONAL (like `enabled`): omitted means the DB default, `false`. This
     * is an owner preference set from REST / the admin SPA only; it is
     * deliberately absent from `CreateScheduleToolInputSchema` so the model
     * cannot opt a user into per-run success spam.
     */
    notifyOnSuccess: Type.Optional(Type.Boolean()),
}, { additionalProperties: false });
/** `PATCH /schedules/:id` body. `expectedUpdatedAt` is the optimistic-concurrency guard. */
export const SchedulePatchBodySchema = Type.Object({
    expectedUpdatedAt: IsoDateTimeSchema,
    scheduleExpr: Type.Optional(ScheduleExprSchema),
    timezone: Type.Optional(ScheduleTimezoneSchema),
    prompt: Type.Optional(SchedulePromptSchema),
    toolAllowlist: Type.Optional(ScheduleToolAllowlistSchema),
    wholePlanPreapproved: Type.Optional(Type.Boolean()),
    enabled: Type.Optional(Type.Boolean()),
    /** COP-134 D3 / ATRU-03 — toggle the `scheduled_succeeded` opt-in. */
    notifyOnSuccess: Type.Optional(Type.Boolean()),
}, { additionalProperties: false });
/**
 * Provenance of a `tf_scheduled_action` row (D-28 / SCUX-10).
 *
 * - `admin`   — created via `POST /schedules` (admin SPA path); Plan 02-02's
 *   migration backfills every existing row to this value.
 * - `copilot` — created via the chat schedule-draft decide path; surfaced in
 *   the admin SPA as a "Created in chat" badge per D-26.
 */
export const ScheduleCreatedBySourceSchema = Type.Union([
    Type.Literal('admin'),
    Type.Literal('copilot'),
]);
/** Owner-visible schedule DTO returned by create/list/get/update. `createdBySource` (D-28) is REQUIRED — Plan 02-02 backfills `'admin'` for existing rows and writes the field on every insert/select path; admin SPA reads it for the "Created in chat" badge (D-26). */
export const ScheduleOwnerDtoSchema = Type.Object({
    id: UuidSchema,
    ownerUserId: UuidSchema,
    trackId: UuidSchema,
    scheduleExpr: ScheduleExprSchema,
    timezone: ScheduleTimezoneSchema,
    prompt: SchedulePromptSchema,
    toolAllowlist: ScheduleToolAllowlistSchema,
    wholePlanPreapproved: Type.Boolean(),
    enabled: Type.Boolean(),
    /** COP-134 D3 / ATRU-03 — REQUIRED on the DTO; the column is `NOT NULL DEFAULT false`. */
    notifyOnSuccess: Type.Boolean(),
    lastRunAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    nextRunAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    createdBy: UuidSchema,
    createdAt: IsoDateTimeSchema,
    updatedBy: UuidSchema,
    updatedAt: IsoDateTimeSchema,
    /** D-28 / SCUX-10: provenance required at the schema layer; admin SPA reads this for the "Created in chat" badge. */
    createdBySource: ScheduleCreatedBySourceSchema,
}, { additionalProperties: false });
export const ScheduleListQuerySchema = Type.Object({
    trackId: Type.Optional(UuidSchema),
    enabled: Type.Optional(Type.Boolean()),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 2000 })),
}, { additionalProperties: false });
export const ScheduleListResponseSchema = Type.Object({
    items: Type.Array(ScheduleOwnerDtoSchema, { maxItems: 100 }),
    nextCursor: Type.Union([Type.String({ minLength: 1, maxLength: 2000 }), Type.Null()]),
}, { additionalProperties: false });
/** COP-076 scheduled run lifecycle values. */
export const ScheduleRunStatusSchema = Type.Union([
    Type.Literal('claimed'),
    Type.Literal('running'),
    Type.Literal('succeeded'),
    Type.Literal('failed'),
    Type.Literal('denied'),
    Type.Literal('waiting_approval'),
    Type.Literal('skipped'),
    Type.Literal('paused'),
]);
/** Redacted owner-visible run summary DTO. Never includes full prompts, tool args, or run payloads. */
export const ScheduleRunSummaryDtoSchema = Type.Object({
    id: UuidSchema,
    scheduleId: UuidSchema,
    ownerUserId: UuidSchema,
    trackId: UuidSchema,
    scheduledFor: IsoDateTimeSchema,
    startedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    finishedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    status: ScheduleRunStatusSchema,
    attemptCount: Type.Integer({ minimum: 0 }),
    nextRetryAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    lastErrorClass: Type.Union([Type.String({ minLength: 1, maxLength: 200 }), Type.Null()]),
    lastErrorMessage: Type.Union([Type.String({ minLength: 1, maxLength: 2000 }), Type.Null()]),
    traceId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    approvalId: Type.Union([UuidSchema, Type.Null()]),
    /**
     * COP-134 D1 / ATRU-04 — the chat session this run wrote.
     *
     * DERIVED, not stored: `tf_scheduled_run` has no `session_id` column. The
     * hub reads the value out of the raw `metadata_jsonb` bag the run writers
     * already fill, so every historical row carries it with no backfill. The
     * bag keeps its own copy — this is a promotion, not a move.
     *
     * COP-131 rule: PRESENT or ABSENT, never `null`. A run whose turn never
     * opened a session omits the key; a literal `null` would read as "the
     * session is gone" instead of "there was never one".
     */
    sessionId: Type.Optional(UuidSchema),
    metadata: JsonObjectSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
}, { additionalProperties: false });
export const ScheduleResponseSchema = Type.Object({
    schedule: ScheduleOwnerDtoSchema,
    latestRun: Type.Union([ScheduleRunSummaryDtoSchema, Type.Null()]),
}, { additionalProperties: false });
export const ScheduleRunsQuerySchema = Type.Object({
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 20 })),
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 2000 })),
}, { additionalProperties: false });
export const ScheduleRunsListResponseSchema = Type.Object({
    items: Type.Array(ScheduleRunSummaryDtoSchema, { maxItems: 20 }),
    nextCursor: Type.Union([Type.String({ minLength: 1, maxLength: 2000 }), Type.Null()]),
}, { additionalProperties: false });
// ===========================================================================
// Phase 02 — Chat Scheduled Actions UX (Plan 02-01)
// ---------------------------------------------------------------------------
// Decisions implemented here: D-04, D-13, D-16, D-19, D-21, D-22, D-23, D-28.
// Every Object schema below applies `additionalProperties: false` to enforce
// the D-32 "no PII smuggling" boundary — the same posture Phase 1 used for
// `PlanPendingPartSchema` and `PlanActivationNoticePartSchema`.
// ===========================================================================
/**
 * SHA-256 payload hash (lowercase hex, 64 chars) — D-16 wire shape.
 *
 * Pattern is enforced at the schema layer so wire-level callers (PATCH and
 * decide endpoints, `tf-schedule-preview-pending` SSE part) cannot pass an
 * uppercase or otherwise mis-shaped hash and have it pretend to match.
 */
export const ScheduleDraftPayloadHashSchema = Type.String({
    pattern: '^[a-f0-9]{64}$',
});
/**
 * Stable, model-facing error codes for the schedule-draft surface — the union
 * RESEARCH § "Stable Error Code Matrix" lines 780-819 lists across three
 * layers. Per RESEARCH line 819, every code lives in a single TS union so the
 * compiler catches drift at every call site.
 *
 * Layer A — Cadence translator (Plan 02-03):
 *   cadence_ambiguous, cadence_below_15_minutes, cadence_composite_unsupported,
 *   cadence_one_shot_unsupported, timezone_required, invalid_timezone
 * Layer B — Tool-input / allowlist (Plan 02-05 `create_schedule` executor):
 *   allowlist_indeterminable, allowlist_user_named_ambiguous,
 *   empty_after_redaction, prompt_too_long, prompt_not_actionable
 * Layer C — Draft route + decide (Plan 02-04):
 *   preview_stale, draft_not_pending, draft_not_found, tool_not_in_allowlist,
 *   empty_allowlist, whole_plan_preapproved_unconfirmed, track_access_denied
 *
 * `track_access_denied` is only thrown at create-time (insertDraft). At
 * patch/decide time, lost track access still collapses to `draft_not_found`
 * to preserve the D-23 no-leak posture for cross-user / cross-session probes.
 * At create-time there is no pre-existing draft to mask, so the honest code
 * is surfaced (polish-backlog P-41.3 — operators with stale/computed-relation
 * tuples were seeing "schedule draft wasn't found" paraphrased back at them).
 */
export const ScheduleDraftErrorCodeSchema = Type.Union([
    // Layer C — Draft route + decide codes
    Type.Literal('preview_stale'),
    Type.Literal('draft_not_pending'),
    Type.Literal('draft_not_found'),
    Type.Literal('tool_not_in_allowlist'),
    Type.Literal('empty_allowlist'),
    Type.Literal('whole_plan_preapproved_unconfirmed'),
    Type.Literal('track_access_denied'),
    // Layer B — Tool-input / allowlist codes
    Type.Literal('allowlist_indeterminable'),
    Type.Literal('allowlist_user_named_ambiguous'),
    Type.Literal('empty_after_redaction'),
    Type.Literal('prompt_too_long'),
    Type.Literal('prompt_not_actionable'),
    // Layer A — Cadence translator codes
    Type.Literal('cadence_ambiguous'),
    Type.Literal('cadence_below_15_minutes'),
    Type.Literal('cadence_composite_unsupported'),
    Type.Literal('cadence_one_shot_unsupported'),
    Type.Literal('timezone_required'),
    Type.Literal('invalid_timezone'),
    // Layer D — Wire-schema drift (P-41.6). The runtime computed a preview
    // part that violated `SchedulePreviewPartSchema`; emitted as a fail-closed
    // error part so the SchedulePreviewCard renders its error state instead
    // of being silently dropped on the floor.
    Type.Literal('preview_schema_invalid'),
]);
/** Cadence-translator-only failure codes (D-04 subset of ScheduleDraftErrorCode). */
export const CadenceTranslationErrorCodeSchema = Type.Union([
    Type.Literal('cadence_ambiguous'),
    Type.Literal('cadence_below_15_minutes'),
    Type.Literal('cadence_composite_unsupported'),
    Type.Literal('cadence_one_shot_unsupported'),
    Type.Literal('timezone_required'),
    Type.Literal('invalid_timezone'),
]);
/**
 * Server-internal cadence-translation result (D-04). Shared here so Plan 02-03
 * unit tests + Plan 02-05 runtime + Plan 02-04 route layer can all spell the
 * type once. Discriminated union on `ok`.
 */
export const CadenceTranslationResultSchema = Type.Union([
    Type.Object({
        ok: Type.Literal(true),
        cron: ScheduleExprSchema,
        timezone: ScheduleTimezoneSchema,
        humanLabel: Type.String({ minLength: 1, maxLength: 200 }),
    }, { additionalProperties: false }),
    Type.Object({
        ok: Type.Literal(false),
        code: CadenceTranslationErrorCodeSchema,
        clarifyingQuestion: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
        suggestion: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
    }, { additionalProperties: false }),
]);
/**
 * `create_schedule` tool input — D-21 model-facing shape.
 *
 * - `cadence` is natural language; D-20 forbids raw cron from the model. The
 *   cadence translator (Plan 02-03) detects raw-cron inputs at runtime and
 *   returns `cadence_ambiguous`. The schema layer itself only enforces
 *   "non-empty string" — semantic rejection is the translator's job.
 * - `timezone` is optional; falls back to `tf_user.timezone` (SCUX-09) before
 *   failing with `timezone_required`.
 * - `prompt` is pre-canonicalization; the runtime canonicalizes per D-11.
 * - `proposedToolAllowlist` is an OPTIONAL model-supplied subset; the server
 *   intersects against the conversation tool history + user-named tools per
 *   D-06 before persisting the draft. It uses the DRAFT allowlist schema
 *   (`minItems: 0`), not the REST body's: on the first turn of a session the
 *   model has nothing concrete to propose, and `createScheduleExecutor`
 *   answers that by persisting an empty draft whose SchedulePreviewCard
 *   carries `data-allowlist-empty=""` for the user to fill. D-06 is enforced
 *   one boundary later, at `decideApprove` (`empty_allowlist`, 422). Reusing
 *   `ScheduleToolAllowlistSchema` here stated the opposite contract; it was
 *   inert until COP-132 removed COP-123 D7 and hub-tool arguments started
 *   being guarded, at which point `[]` rejected the call before the executor
 *   ran and restored the T-41.1 dead end. (COP-087, COP-133, T-41.1.)
 *
 * Group 41 / T-41.2: `trackId` is deliberately NOT a model-facing field.
 * The executor sources it from `deps.ctx.trackId` (the session's track) so
 * the model cannot hallucinate one — observed in production sessions where
 * the agent emitted random UUIDs after the user retried.
 *
 * Group 41 / P-41.8: `wholePlanPreapproved` is deliberately NOT a model-facing
 * field either. Models trained on adjacent action shapes opted into
 * preapproval without the user asking, which then forced the consent gate
 * open in the SchedulePreviewCard and blocked Approve until the user found
 * and clicked Cancel on a panel that should not have existed. Preapproval is
 * a user-only opt-in: the card surfaces a checkbox post-render that PATCHes
 * the persisted draft.
 */
export const CreateScheduleToolInputSchema = Type.Object({
    cadence: Type.String({ minLength: 1, maxLength: 500 }),
    timezone: Type.Optional(ScheduleTimezoneSchema),
    prompt: SchedulePromptSchema,
    proposedToolAllowlist: Type.Optional(ScheduleDraftToolAllowlistSchema),
}, { additionalProperties: false });
/**
 * `create_schedule` model-facing return on the *preview* path: only the draft
 * id, intentionally. No canonical prompt, no cron string, no timezone — the
 * model already supplied those, and re-emitting them on this tool result would
 * just bloat the context window (and risk redaction-drift surface).
 */
export const CreateScheduleToolPreviewOutputSchema = Type.Object({
    scheduleDraftId: UuidSchema,
}, { additionalProperties: false });
/**
 * `list_my_schedules` tool input (RESEARCH Open Q#3 recommendation).
 *
 * All fields optional. `limit` bounded to ≤ 50 to keep tool-result payloads
 * small in chat context.
 */
export const ListMySchedulesToolInputSchema = Type.Object({
    trackId: Type.Optional(UuidSchema),
    enabled: Type.Optional(Type.Boolean()),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 50 })),
}, { additionalProperties: false });
/**
 * `list_my_schedules` tool output — D-13 verbatim. NO `prompt`, NO
 * `canonicalPrompt`, NO `promptSummary` at any depth. If a future requirements
 * pass needs prompt content surfaced, the documented upgrade path is a new
 * `get_my_schedule(id)` companion tool with its own redaction posture (D-15).
 */
export const ListMySchedulesToolOutputEntrySchema = Type.Object({
    id: UuidSchema,
    cadenceLabel: Type.String({ minLength: 1, maxLength: 200 }),
    nextRunAt: IsoDateTimeSchema,
    enabled: Type.Boolean(),
    toolCount: Type.Integer({ minimum: 0 }),
    trackId: UuidSchema,
    createdBySource: ScheduleCreatedBySourceSchema,
}, { additionalProperties: false });
export const ListMySchedulesToolOutputSchema = Type.Object({
    schedules: Type.Array(ListMySchedulesToolOutputEntrySchema, { maxItems: 50 }),
    truncated: Type.Boolean(),
}, { additionalProperties: false });
/**
 * `tf-schedule-preview-pending` SSE part — RESEARCH § Common Operation 2,
 * verbatim shape. Emitted by the `create_schedule` executor success branch.
 *
 * `availableTools` is bounded to ≤ 200 entries; the chat-side UI dropdown is
 * the caller's annotated track tool set (full set per D-07).
 */
export const SchedulePreviewPartSchema = Type.Object({
    type: Type.Literal('tf-schedule-preview-pending'),
    scheduleDraftId: UuidSchema,
    cadenceLabel: Type.String({ minLength: 1, maxLength: 200 }),
    cronExpr: ScheduleExprSchema,
    timezone: ScheduleTimezoneSchema,
    canonicalPrompt: SchedulePromptSchema,
    toolAllowlist: ScheduleDraftToolAllowlistSchema,
    availableTools: Type.Array(ScheduleToolNameSchema, { maxItems: 200 }),
    wholePlanPreapproved: Type.Boolean(),
    wholePlanPreapprovedWarningText: Type.String({ minLength: 1, maxLength: 500 }),
    /**
     * COP-096 rule 5 — whether the hub is configured to expose the
     * "Enable whole-plan preapproval" control on this card
     * (`SCHEDULE_WHOLE_PLAN_PREAPPROVAL_ENABLED`). Optional for wire-format
     * back-compat; absent/false → the card hides the whole-plan section and
     * the schedule is created under the allowlist-as-approval model.
     */
    wholePlanPreapprovalUiEnabled: Type.Optional(Type.Boolean()),
    nextRunAt: IsoDateTimeSchema,
    payloadHash: ScheduleDraftPayloadHashSchema,
    expiresAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/**
 * `tf-schedule-preview-resolved` SSE part — emitted by the next turn's
 * `pendingScheduleDraftId` continuation branch BEFORE injecting the synthetic
 * tool result (RESEARCH Open Q#5 recommendation). Terminal decisions only —
 * `pending` is excluded because `pending` cards stay live; only approve /
 * reject / expire trigger terminalization.
 *
 * The cross-message terminal-state collector in hub-client (`collectSchedule
 * PreviewTerminalStates`, Plan 02-06) consumes this part.
 *
 * D-13 / D-32: NO `canonicalPrompt`, NO `cron`, NO `timezone`, NO allowlist
 * contents. The card has already rendered all that on the prior turn; on
 * resolution we only need the discriminator + (when approved) the persisted
 * `scheduleId` so the UI can deep-link to the admin SPA, and `nextRunAt` to
 * render "next run at …" copy without a follow-up fetch.
 */
export const SchedulePreviewResolvedPartSchema = Type.Object({
    type: Type.Literal('tf-schedule-preview-resolved'),
    scheduleDraftId: UuidSchema,
    decision: Type.Union([
        Type.Literal('approved'),
        Type.Literal('rejected'),
        Type.Literal('expired'),
    ]),
    scheduleId: Type.Optional(UuidSchema),
    nextRunAt: Type.Optional(IsoDateTimeSchema),
}, { additionalProperties: false });
/**
 * `tf-schedule-preview-error` SSE part — emitted when the runtime hits an
 * unrecoverable mid-stream condition that wasn't anticipated by the normal
 * decide-endpoint flow (e.g. `draft_not_found` during a continuation, schema
 * drift, defensive guards).
 *
 * `scheduleDraftId` is OPTIONAL because the not-found case lacks an ID — the
 * draft was never visible under the caller's RLS scope.
 */
export const SchedulePreviewErrorPartSchema = Type.Object({
    type: Type.Literal('tf-schedule-preview-error'),
    scheduleDraftId: Type.Optional(UuidSchema),
    code: ScheduleDraftErrorCodeSchema,
}, { additionalProperties: false });
/**
 * `PATCH /schedule-drafts/:id` body — D-18 hash-bound edit.
 *
 * Every editable field is OPTIONAL so callers can submit narrow patches.
 * `payloadHash` is REQUIRED — it's the optimistic-concurrency guard; mismatch
 * returns 409 `preview_stale`. The server intersects `toolAllowlist` against
 * the caller's annotated track tool set on every PATCH (D-18).
 */
export const UpdateScheduleDraftBodySchema = Type.Object({
    prompt: Type.Optional(SchedulePromptSchema),
    toolAllowlist: Type.Optional(ScheduleToolAllowlistSchema),
    wholePlanPreapproved: Type.Optional(Type.Boolean()),
    /** D-26: paired explicit-opt-in flag. decideApprove rejects with `whole_plan_preapproved_unconfirmed` when wholePlanPreapproved=true and this is false. */
    wholePlanPreapprovedConfirmed: Type.Optional(Type.Boolean()),
    cadence: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
    timezone: Type.Optional(ScheduleTimezoneSchema),
    payloadHash: ScheduleDraftPayloadHashSchema,
}, { additionalProperties: false });
/**
 * `GET /schedule-drafts/:id` + `PATCH /schedule-drafts/:id` 200 response.
 * Shared so the typed `HubClient` (`packages/hub-client/src/endpoints.ts`)
 * can declare `response: ScheduleDraftResponseSchema` and have `res.json()`
 * actually surface to the caller. Without a `response` declaration, the
 * HubClient silently returns `undefined` — the source of P-41.5: the card's
 * `setPayloadHash(response.payloadHash)` after PATCH became a no-op, so the
 * next Approve sent the original insert-time hash and the server 409'd as
 * `preview_stale`.
 */
export const ScheduleDraftResponseSchema = Type.Object({
    id: UuidSchema,
    payloadHash: ScheduleDraftPayloadHashSchema,
    expiresAt: Type.String({ format: 'date-time' }),
    payload: Type.Object({
        cron: Type.String({ minLength: 1, maxLength: 200 }),
        timezone: Type.String({ minLength: 1, maxLength: 100 }),
        canonicalPrompt: Type.String({ minLength: 1, maxLength: 16384 }),
        toolAllowlist: Type.Array(Type.String({ minLength: 1, maxLength: 200 })),
        wholePlanPreapproved: Type.Boolean(),
        wholePlanPreapprovedConfirmed: Type.Boolean(),
        cadenceLabel: Type.String({ minLength: 0, maxLength: 200 }),
    }, { additionalProperties: false }),
}, { additionalProperties: false });
/**
 * `POST /schedule-drafts/:id/decide` body — D-19 decide semantics.
 *
 * `decision` is `'approve' | 'reject'`. `payloadHash` REQUIRED on both
 * decisions; mismatch returns 409 `preview_stale`. Approve revalidates cron,
 * timezone, allowlist, track ownership AT decide time (defense against late
 * changes to the annotated track tool set).
 */
export const DecideScheduleDraftBodySchema = Type.Object({
    decision: Type.Union([Type.Literal('approve'), Type.Literal('reject')]),
    payloadHash: ScheduleDraftPayloadHashSchema,
}, { additionalProperties: false });
/**
 * `POST /schedule-drafts/:id/decide` response — D-19 idempotent.
 *
 * - `approve` returns `{ status: 'approved', scheduleId, nextRunAt, enabled }`
 *   on first-call and on duplicate approve (same `scheduleId` echo per D-19
 *   "mirrors existing route conventions").
 * - `reject` returns `{ status: 'rejected' }` with no further fields.
 */
export const DecideScheduleDraftResponseSchema = Type.Object({
    status: Type.Union([Type.Literal('approved'), Type.Literal('rejected')]),
    scheduleId: Type.Optional(UuidSchema),
    nextRunAt: Type.Optional(IsoDateTimeSchema),
    enabled: Type.Optional(Type.Boolean()),
}, { additionalProperties: false });
//# sourceMappingURL=schedules.js.map